import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { parseCoordinatesFromString } from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoShipDeckBulkheadJSON {
  type: GeckoFeatureModelType.ShipDeckBulkhead
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
}

export const NewGeckoShipDeckBulkheadJSON: GeckoShipDeckBulkheadJSON = {
  type: GeckoFeatureModelType.ShipDeckBulkhead,
  name: 'New Ship Deck Bulkhead',
  feature_geometry: {
    coordinates: [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ],
  },
}

export class GeckoShipDeckBulkhead extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckBulkhead
  readonly coordinates: {
    readonly x: GeckoMeasurement
    readonly y: GeckoMeasurement
  }[]

  /**
   *
   * @param id
   * @param name
   * @param coordinates
   */

  private constructor(
    id: string,
    name: string,
    coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[]
  ) {
    super(id)
    this.name = name
    this.coordinates = coordinates
  }

  public static CreateFromJSON(
    componentID: string,
    bulkheadID: string,
    bulkheadJSON: GeckoShipDeckBulkheadJSON
  ): GeckoShipDeckBulkhead {
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const bulkheadCoordinates = bulkheadJSON.feature_geometry.coordinates.map(
      (coord) => ({
        x: GeckoMeasurement.CreateFromValue(coord.x, polygonUnits.x.unit),
        y: GeckoMeasurement.CreateFromValue(coord.y, polygonUnits.y.unit),
      })
    )

    const bulkheadName = bulkheadJSON.name as string

    return new GeckoShipDeckBulkhead(
      bulkheadID,
      bulkheadName,
      bulkheadCoordinates
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckBulkheadJSON>
  ): Map<string, GeckoShipDeckBulkhead> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckBulkhead.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckBulkhead>())
  }

  asJSON() {
    return generateBulkhead(this.name, this.coordinates)
  }
}

export function generateBulkhead(
  name: string,
  coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[]
): GeckoShipDeckBulkheadJSON {
  return {
    name: name,
    type: GeckoFeatureModelType.ShipDeckBulkhead,
    feature_geometry: {
      coordinates: coordinates.map((coord) => ({
        x: coord.x.toNumber(GeckoMeasurementUnit.Meters),
        y: coord.y.toNumber(GeckoMeasurementUnit.Meters),
      })),
    },
  }
}

export const ShipDeckBulkheadFormFields = (
  bulkheadJSON: GeckoShipDeckBulkheadJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Bulkhead Name',
      value: bulkheadJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: bulkheadJSON.feature_geometry.coordinates
        .map((coord) => `[${coord.x}, ${coord.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(4),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
  ]
}

export const ShipDeckBulkheadJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getFormFieldByDataKey(formFields, 'coordinates')
    ?.value as string

  return generateBulkhead(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    parseCoordinatesFromString(coordinates).map(({ x, y }) => ({
      x: GeckoMeasurement.CreateFromValue(x, GeckoMeasurementUnit.Meters),
      y: GeckoMeasurement.CreateFromValue(y, GeckoMeasurementUnit.Meters),
    }))
  )
}

export const ShipDeckBulkheadFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckBulkhead,
  title: 'Bulkheads',
  singularName: 'Bulkhead',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to enter coordinates to render a bulkhead correctly, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a> on polygonal plates.',
}
